"use client"

import { useCallback, useMemo, useState } from "react"
import { STARTING_NODES, type LexicalText } from "@/schema"

import { useEmailSendingIntegration } from "@/hooks/api/email_integrations"
import { buildSendableAddresses, type SendableAddress } from "@/components/email/sender-addresses"
import {
  containsSignatureNodes,
  getSignatureForEmail,
  removeTrailingSignatureNodes,
  signatureHtmlFromContent,
} from "@/components/email/signature-utils"
import { EditorRefHandle } from "@/components/lexical/editors/email"
import { htmlToNodes, mergeLexicalContents } from "@/components/lexical/serializer"

export { buildSendableAddresses, getDefaultAddress } from "@/components/email/sender-addresses"
export type { SendableAddress } from "@/components/email/sender-addresses"

export interface TemplateAttachment {
  signed_id: string
  file_name: string
}

interface UseEmailComposeLogicParams {
  content: LexicalText | undefined
  setContent: (content: LexicalText) => void
  editorRef?: React.RefObject<EditorRefHandle | null>
}

export const resolveSignatureNodes = (signature?: LexicalText | null) => {
  if (!signature) return undefined
  if (signature.nodes) return signature.nodes
  if (signature.html) {
    return htmlToNodes(signature.html) ?? undefined
  }
  return undefined
}

export function useEmailComposeLogic({ content, setContent, editorRef }: UseEmailComposeLogicParams) {
  const [selectedAddress, setSelectedAddress] = useState<SendableAddress | null>(null)
  const [fromPopoverOpen, setFromPopoverOpen] = useState(false)
  const [templateAttachments, setTemplateAttachments] = useState<TemplateAttachment[]>([])

  const {
    defaultSendingIntegration,
    sendingIntegrations,
    emailIntegrations,
    currentUser,
    isSenderReady,
    isSenderError,
    refetchSender,
  } = useEmailSendingIntegration()

  const allSendableAddresses = useMemo(() => buildSendableAddresses(sendingIntegrations, true), [sendingIntegrations])

  const getIntegrationById = useCallback(
    (integrationId: string) => emailIntegrations?.find((i) => i.id === integrationId),
    [emailIntegrations]
  )

  const selectedIntegration = useMemo(
    () => (selectedAddress ? getIntegrationById(selectedAddress.integrationId) : undefined),
    [selectedAddress, getIntegrationById]
  )

  const getSignatureNodes = useCallback(
    (integrationId?: string, fromEmail?: string) => {
      const integration = integrationId ? getIntegrationById(integrationId) : selectedIntegration
      const signature = getSignatureForEmail(integration, fromEmail ?? selectedAddress?.email)?.content
      return resolveSignatureNodes(signature)
    },
    [getIntegrationById, selectedIntegration, selectedAddress?.email]
  )

  const getSignatureHtml = useCallback(
    (integrationId?: string, fromEmail?: string) => {
      const integration = integrationId ? getIntegrationById(integrationId) : selectedIntegration
      const signature = getSignatureForEmail(integration, fromEmail ?? selectedAddress?.email)?.content
      return signatureHtmlFromContent(signature)
    },
    [getIntegrationById, selectedIntegration, selectedAddress?.email]
  )

  const contentContainsSignature = useCallback(
    (contentNodes?: typeof STARTING_NODES, integrationId?: string, fromEmail?: string) => {
      const signatureNodes = getSignatureNodes(integrationId, fromEmail)
      return containsSignatureNodes(contentNodes, signatureNodes)
    },
    [getSignatureNodes]
  )

  const appendSignature = useCallback(
    (contentNodes?: typeof STARTING_NODES, integrationId?: string, fromEmail?: string) => {
      const signatureNodes = getSignatureNodes(integrationId, fromEmail)
      if (!signatureNodes) return contentNodes

      return mergeLexicalContents([{ nodes: contentNodes }, { nodes: signatureNodes }])
    },
    [getSignatureNodes]
  )

  const removeSignature = useCallback(
    (contentNodes?: typeof STARTING_NODES, integrationId?: string, fromEmail?: string) => {
      const signatureNodes = getSignatureNodes(integrationId, fromEmail)
      return removeTrailingSignatureNodes(contentNodes, signatureNodes)
    },
    [getSignatureNodes]
  )

  const handleAddressChange = useCallback(
    (newAddress: SendableAddress) => {
      const oldIntegrationId = selectedAddress?.integrationId
      const newIntegrationId = newAddress.integrationId

      if (oldIntegrationId !== newIntegrationId || selectedAddress?.email !== newAddress.email) {
        const cleanedContent = removeSignature(content?.nodes, oldIntegrationId, selectedAddress?.email)
        const newSignatureNodes = getSignatureNodes(newIntegrationId, newAddress.email)

        if (newSignatureNodes) {
          const merged = mergeLexicalContents([{ nodes: cleanedContent }, { nodes: newSignatureNodes }])
          setContent({ nodes: merged, preferred_format: "html" } as LexicalText)
        } else if (cleanedContent) {
          setContent({ nodes: cleanedContent, preferred_format: "html" } as LexicalText)
        }
      }

      setSelectedAddress(newAddress)
      setFromPopoverOpen(false)
    },
    [selectedAddress, content?.nodes, removeSignature, getSignatureNodes, setContent]
  )

  const handleApplyTemplate = useCallback(
    (templateSubject: string, templateBody: LexicalText, attachments?: TemplateAttachment[]) => {
      const signatureNodes = getSignatureNodes()
      const bodyNodes = templateBody.nodes ?? htmlToNodes(templateBody.html || "") ?? STARTING_NODES
      const mergedNodes = signatureNodes
        ? mergeLexicalContents([{ nodes: bodyNodes }, { nodes: signatureNodes }])
        : bodyNodes

      const finalContent = {
        nodes: mergedNodes,
        preferred_format: "html",
      } as LexicalText

      setContent(finalContent)
      editorRef?.current?.updateContent(finalContent)

      if (attachments?.length) {
        setTemplateAttachments(attachments)
      } else {
        setTemplateAttachments([])
      }

      return templateSubject
    },
    [setContent, getSignatureNodes, editorRef]
  )

  const removeTemplateAttachment = useCallback((signedId: string) => {
    setTemplateAttachments((prev) => prev.filter((a) => a.signed_id !== signedId))
  }, [])

  return {
    selectedAddress,
    setSelectedAddress,
    allSendableAddresses,
    fromPopoverOpen,
    setFromPopoverOpen,
    handleAddressChange,
    selectedIntegration,
    getIntegrationById,
    currentUser,
    isSenderReady,
    isSenderError,
    refetchSender,
    defaultSendingIntegration,
    sendingIntegrations,
    emailIntegrations,
    templateAttachments,
    setTemplateAttachments,
    removeTemplateAttachment,
    handleApplyTemplate,
    getSignatureNodes,
    getSignatureHtml,
    contentContainsSignature,
    appendSignature,
    removeSignature,
  }
}
